// lib
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from "@material-ui/core";
import { Close, Group, LibraryBooks, Person, Room, Search as SearchIcon } from "@material-ui/icons";
// src
import { ResourceType, FETCHABLE_RESOURCES } from "config/constants";
import { forgePresetFromResource } from "utils/presets";
import Courses from "./tabs/Courses";
import Groups from "./tabs/Groups";
import People from "./tabs/People";
import Rooms from "./tabs/Rooms";

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return children;
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const getTabComponent = (type) => {
  if (type === ResourceType.COURSE) return Courses;
  if (type === ResourceType.GROUP) return Groups;
  if (type === ResourceType.PERSON) return People;
  if (type === ResourceType.ROOM) return Rooms;
  return null;
};

const getIcon = (type) => {
  if (type === ResourceType.COURSE) return <LibraryBooks className="icon-type" />;
  if (type === ResourceType.GROUP) return <Group className="icon-type" />;
  if (type === ResourceType.PERSON) return <Person className="icon-type" />;
  if (type === ResourceType.ROOM) return <Room className="icon-type" />;
  return null;
};

const NOT_ALLOWED = /[^\p{L}\d\s-]/gu;

const Search = ({
  // data props
  isSearchOpen,
  isSmall,
  results,
  selfTranslate,
  // dispatch props
  manageAddToLocals,
  clearSearch,
  fetchSearch,
  hideSearch,
}) => {
  const searchRef = useRef(null);
  const [displayables, setDisplayables] = useState({});
  const [selecteds, setSelecteds] = useState([]);
  const [tab, setTab] = useState(0);
  const [timeoutID, setTimeoutId] = useState();
  const { t } = useTranslation();
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => {
        if (searchRef.current) searchRef.current.focus();
      }, 0);
    }
  }, [isSearchOpen]);
  useEffect(() => {
    setDisplayables({ ...results });
  }, [results]);

  // UX
  const handleResetSearch = () => {
    hideSearch();
    setSelecteds([]);
    clearSearch();
  };
  const handleAddToLocalPresets = () => {
    const presets = selecteds.map(forgePresetFromResource);
    manageAddToLocals(presets);
    handleResetSearch();
  };

  // UX
  const handleChangeTab = (event, newTab) => setTab(newTab);

  const handleSearch = (e) => {
    const query = e.target.value.replace(NOT_ALLOWED, "");
    if (timeoutID) clearTimeout(timeoutID);
    if (query.length > 1) {
      setTimeoutId(setTimeout(() => fetchSearch(query), 500));
    }
  };

  const updateSelectedsAndDisplayables = (newSelecteds) => {
    const newSelectedIds = newSelecteds.map(({ id }) => id);
    const newDisplayables = FETCHABLE_RESOURCES.reduce((acc, { type }) => {
      if (results[type]) {
        acc[type] = results[type].filter(({ id }) => !newSelectedIds.includes(id));
      }
      return acc;
    }, displayables);
    setSelecteds(newSelecteds);
    setDisplayables(newDisplayables);
  };
  const createHandleSelect = (p) => () => {
    updateSelectedsAndDisplayables([...selecteds, p]);
  };
  const createHandleUnSelect = (p) => () => {
    updateSelectedsAndDisplayables(selecteds.filter(({ id }) => id !== p.id));
  };

  const addLabel =
    selecteds.length === 0 ? t("actions.add") : t("actions.addN", { n: selecteds.length });

  const getLabelOf = (type, plural) => {
    const count =
      displayables[type] && displayables[type].length ? (
        <Chip size="small" label={displayables[type].length} />
      ) : null;
    return (
      <div className="tab-heading">
        {getIcon(type)}
        <span>{t(`resources.${plural}`)}</span>
        {count}
      </div>
    );
  };

  const renderResultsOfType = (type) => {
    const Component = getTabComponent(type);
    if (Component) {
      return (
        <Component
          createHandleSelect={createHandleSelect}
          items={displayables[type]}
          selfTranslate={selfTranslate}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Dialog
        className="modal-search"
        fullScreen={isSmall}
        open={isSearchOpen}
        onClose={handleResetSearch}
      >
        <IconButton className="close" onClick={handleResetSearch}>
          <Close />
        </IconButton>
        <DialogTitle disableTypography>
          <div className="search">
            <TextField
              inputRef={searchRef}
              autoFocus
              fullWidth
              onChange={handleSearch}
              type="search"
              InputProps={{
                autoFocus: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="resources">
            <Tabs
              variant="scrollable"
              scrollButtons={false}
              value={tab}
              onChange={handleChangeTab}
              textColor="primary"
              centered
            >
              {FETCHABLE_RESOURCES.map(({ type, plural }) => (
                <Tab key={type} label={getLabelOf(type, plural)} />
              ))}
            </Tabs>
          </div>
        </DialogTitle>
        <DialogContent>
          {FETCHABLE_RESOURCES.map(({ type }, index) => (
            <TabPanel key={type} value={tab} index={index}>
              {renderResultsOfType(type)}
            </TabPanel>
          ))}
        </DialogContent>
        <DialogActions className="selecteds">
          {selecteds.length > 0 && (
            <div className="chips-container">
              {selecteds.map((p) => (
                <Chip
                  className="chip"
                  key={p.id}
                  label={selfTranslate(p.display || p.name)}
                  clickable
                  onClick={createHandleUnSelect(p)}
                  onDelete={createHandleUnSelect(p)}
                  deleteIcon={<Close />}
                />
              ))}
            </div>
          )}
        </DialogActions>
        <DialogActions className="actions">
          <Button autoFocus onClick={handleResetSearch}>
            {t("actions.cancel")}
          </Button>
          <Button
            onClick={handleAddToLocalPresets}
            color="primary"
            autoFocus
            disabled={selecteds.length === 0}
          >
            {addLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

Search.propTypes = {
  // data props
  isSearchOpen: PropTypes.bool.isRequired,
  isSmall: PropTypes.bool.isRequired,
  results: PropTypes.object.isRequired,
  selfTranslate: PropTypes.func.isRequired,
  // dispatch props
  manageAddToLocals: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
  fetchSearch: PropTypes.func.isRequired,
  hideSearch: PropTypes.func.isRequired,
};

export default Search;
